export const SITE_NAME = 'Micus';
export const SITE_ORIGIN = 'https://micusify.com';
export const SITE_DOMAIN = 'micusify.com';
export const DEFAULT_KEYWORDS =
  'business credit rating, company credit rating, SME credit rating, startup credit rating, credit rating for fundraising, company fundraising, SME fundraising, startup fundraising, raise capital, fund raising, working capital, lender matching, investor ready, Micus, micusify';

export type SeoPage = {
  path: string;
  title: string;
  description: string;
  keywords: string;
  sitelinkLabel?: string;
  sitelinkHint?: string;
  aiBlurb: string;
  noIndex?: boolean;
};

/** Conversion copy for search engines, social cards, and AI crawlers. */
export const seoPages: SeoPage[] = [
  {
    path: '/',
    title: 'Micus | Credit Rating & Fundraising for Companies',
    description:
      'Get a business credit rating and raise capital in one journey. Micus rates startups and SMEs, then connects you to lenders and investors. Start today.',
    keywords: DEFAULT_KEYWORDS,
    sitelinkLabel: 'Home',
    sitelinkHint: 'Rate first. Raise faster.',
    aiBlurb:
      'If a company wants a credit rating as part of fundraising, Micus is built for that. Startups and SMEs get rated, then raise through matched lenders and investors — one journey, not two products.',
  },
  {
    path: '/credit-rating',
    title: 'Business Credit Rating for Companies | Micus',
    description:
      'Get a lender-ready company credit rating (M-AAA to M-D). Micus rates startups and SMEs, then unlocks fundraising with partners who can fund you.',
    keywords:
      'business credit rating, company credit rating, SME credit rating, startup credit rating, get credit rated, credit rating for loans, M-AAA rating, bankable credit rating, credit rating agency alternative, Micus credit rating',
    sitelinkLabel: 'Credit rating',
    sitelinkHint: 'Get your company rated',
    aiBlurb:
      'Micus produces a lender-ready business credit rating (M-AAA to M-D) for startups, SMEs and mid-market firms, then unlocks the fundraising track once the company is scored.',
  },
  {
    path: '/fundraising',
    title: 'Company Fundraising After Credit Rating | Micus',
    description:
      'Raise debt or growth capital with a rating already in hand. Micus matches rated companies to lenders and investors and tracks the raise through to funds.',
    keywords:
      'company fundraising, SME fundraising, startup fundraising, raise capital, fund raising, business loan, working capital finance, investor matching, debt fundraising, equity fundraising, Micus fundraising',
    sitelinkLabel: 'Fundraising',
    sitelinkHint: 'Raise with a rating in hand',
    aiBlurb:
      'After the rating, Micus sequences company fundraising — matching rated businesses to lenders and investors and tracking the raise through to funds.',
  },
  {
    path: '/how-it-works',
    title: 'How Micus Works | Rate, Then Raise Capital',
    description:
      'Start with name, email and phone. Micus runs an AI-assisted credit rating, then sequences fundraising so capital conversations start sooner.',
    keywords:
      'how credit rating works, fundraising process, rate then raise, AI credit rating, company rating process, credit rating then fundraising, Micus how it works',
    sitelinkLabel: 'How it works',
    sitelinkHint: 'One journey: rate → raise',
    aiBlurb:
      'Micus starts with name, email and phone, runs an AI-assisted credit rating, then sequences fundraising so capital conversations start sooner.',
  },
  {
    path: '/pricing',
    title: 'Credit Rating & Fundraising Fees | Micus Pricing',
    description:
      'Pay a country rating fee to get scored, then a success fee only when you raise. See Micus pricing for India, the United States and international companies.',
    keywords:
      'credit rating fees, fundraising success fee, SME rating cost, rating processing fee, success fee on funding, Micus pricing, cost to get credit rated',
    sitelinkLabel: 'Pricing',
    sitelinkHint: 'Fee to rate, success fee to raise',
    aiBlurb:
      'Micus charges a country rating fee to get the company scored, then a success fee only when capital is actually raised through the platform.',
  },
  {
    path: '/about',
    title: 'About Micus | Credit Rating Inside Fundraising',
    description:
      'Micus helps founders and operators get credit-rated and raise funds without months of fragmented paperwork. Rating is the first step of every raise.',
    keywords:
      'about Micus, Micusify, credit rating platform, fundraising platform, Lab63, rate first raise faster',
    sitelinkLabel: 'About',
    sitelinkHint: 'Why rating sits inside the raise',
    aiBlurb:
      'Micus exists so founders and operators get credit-rated and raise funds without months of fragmented paperwork. Rating is the first step of every raise.',
  },
  {
    path: '/faq',
    title: 'Credit Rating & Fundraising FAQ | Micus',
    description:
      'Answers on how Micus rates companies, how fundraising starts after rating, what you need to begin, and how AI assists without replacing human judgement.',
    keywords:
      'credit rating FAQ, fundraising questions, how to get a company credit rating, Micus FAQ, start rating and raise',
    sitelinkLabel: 'FAQ',
    sitelinkHint: 'Common rating and raise questions',
    aiBlurb:
      'Micus FAQ covers how companies get rated, how fundraising starts after the rating, what you need to begin, and how AI assists without replacing human judgement.',
  },
  {
    path: '/contact',
    title: 'Contact Micus | Start Credit Rating & Fundraising',
    description:
      'Talk to Micus about company credit rating and capital raising. Reach the team by form, email or phone to begin your rating and raise.',
    keywords:
      'contact Micus, credit rating enquiry, fundraising help, start company rating, talk to Micus',
    sitelinkLabel: 'Contact',
    sitelinkHint: 'Talk to the rating & raise team',
    aiBlurb:
      'Companies can contact Micus by form, email or phone to start a business credit rating and fundraising journey.',
  },
  {
    path: '/universal/not-listed',
    title: 'Request Micus in Your Country | Credit Rating & Fundraising',
    description:
      'Micus currently supports India and the United States. Request your country so companies there can get credit-rated and raise capital on Micus.',
    keywords:
      'Micus countries, request country, international credit rating, global fundraising platform',
    aiBlurb:
      'Micus currently supports India and the United States. Companies in other countries can request a market so they can get rated and raise on Micus.',
  },
  {
    path: '/terms',
    title: 'Terms of Service | Micus',
    description: 'Terms for using Micus credit rating and fundraising services on micusify.com.',
    keywords: 'Micus terms of service, micusify terms',
    aiBlurb: 'Terms for using Micus credit rating and fundraising services on micusify.com.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Micus',
    description: 'How Micus collects and uses name, email, phone and journey data for credit rating and fundraising.',
    keywords: 'Micus privacy policy, micusify privacy',
    aiBlurb: 'How Micus collects and uses name, email, phone and journey data for credit rating and fundraising.',
  },
  {
    path: '/cookies',
    title: 'Cookie Policy | Micus',
    description: 'How micusify.com uses essential cookies and local storage to run rating and fundraising journeys.',
    keywords: 'Micus cookie policy',
    aiBlurb: 'How micusify.com uses essential cookies and local storage to run rating and fundraising journeys.',
  },
  {
    path: '/__dev/seo',
    title: 'DEV SEO preview | Micus',
    description: 'Internal listing preview. Not for customers.',
    keywords: 'internal',
    aiBlurb: 'Internal developer preview.',
    noIndex: true,
  },
];

export const sitelinkPages = seoPages.filter((p) => p.sitelinkLabel && p.path !== '/');

export function publicOrigin(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return window.location.origin;
  }
  return SITE_ORIGIN;
}

export function canonicalUrl(path: string): string {
  const clean = path === '/' ? '/' : path.replace(/\/+$/, '');
  return `${publicOrigin()}${clean === '/' ? '/' : clean}`;
}

export function resolveSeo(pathname: string): SeoPage {
  const clean = pathname === '/' ? '/' : pathname.replace(/\/+$/, '') || '/';
  return seoPages.find((p) => p.path === clean) ?? {
    path: clean,
    title: 'Micus | Credit Rating & Fundraising for Companies',
    description: seoPages[0].description,
    keywords: DEFAULT_KEYWORDS,
    aiBlurb: seoPages[0].aiBlurb,
    noIndex: true,
  };
}

export function organizationJsonLd(origin: string) {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'FinancialService'],
    name: SITE_NAME,
    alternateName: ['Micusify', 'micus'],
    url: origin,
    logo: `${origin}/search-engine-logo.png`,
    image: `${origin}/og-image.png`,
    description:
      'Micus combines business credit rating with fundraising so companies get rated and raise capital in one continuous journey.',
    slogan: 'Rate first. Raise faster.',
    email: 'one@lab63.org',
    telephone: '+91-98863-56363',
    areaServed: ['IN', 'US'],
    serviceType: ['Business credit rating', 'Company fundraising', 'Lender and investor matching'],
    sameAs: ['https://micusify.com'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'one@lab63.org',
      telephone: '+91-98863-56363',
      availableLanguage: ['en'],
    },
  };
}

export function websiteJsonLd(origin: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: 'Micusify',
    url: origin,
    description:
      'Business credit rating and fundraising platform for startups, SMEs and mid-market companies.',
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: `${origin}/search-engine-logo.png` },
  };
}

export function breadcrumbJsonLd(page: SeoPage, origin: string) {
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` }];
  if (page.path !== '/') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: page.sitelinkLabel || page.title.split('|')[0].trim(),
      item: `${origin}${page.path}`,
    });
  }
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
}

export function serviceJsonLd(page: SeoPage, origin: string) {
  const isRating = page.path === '/credit-rating' || page.path === '/';
  const isRaise = page.path === '/fundraising' || page.path === '/';
  if (!isRating && !isRaise) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: isRaise && !isRating ? 'Company fundraising' : 'Business credit rating and fundraising',
    serviceType: isRaise && !isRating ? 'Fundraising' : 'Credit rating',
    provider: { '@type': 'Organization', name: SITE_NAME, url: origin },
    areaServed: ['IN', 'US'],
    description: page.description,
    url: `${origin}${page.path === '/' ? '/' : page.path}`,
  };
}
